/**
 * 导入服务（人工审核闸门）
 * ------------------------------------------------------------
 * 流程强制为三步，**不允许跳过预览**：
 *   1. saveDraft(draft)      → 只写入 imports 集合，status='pending_review'
 *   2. 用户在预览页逐条修改 / 删除 / 标记确认
 *   3. commitImport(draftId) → 才把内容写进正式内容集合，status='confirmed'
 *
 * 教材页、练习页只读取 status='confirmed' 的内容，所以草稿永远不会被学生看到。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { makeTextbook, makeUnit, makeWord, makeSentence, makeGrammar, makeQuestion } from '../core/models.js';
import { invalidateCache } from './dataSource.js';
import { attachQuestionRefs } from './knowledge.js';
import { emit, EVENTS } from '../core/events.js';
import { nowISO, uid } from '../utils/index.js';

/* ------------------------------ 草稿 ------------------------------ */

export async function saveDraft(draft) {
  const storage = getStorage();
  const row = { ...draft, status: 'pending_review', updatedAt: nowISO() };
  await storage.put(COLLECTIONS.IMPORTS, row);
  return row;
}

export async function getDraft(id) {
  return getStorage().get(COLLECTIONS.IMPORTS, id);
}

export async function listDrafts({ status = '' } = {}) {
  const rows = await getStorage().list(COLLECTIONS.IMPORTS);
  const filtered = status ? rows.filter(r => r.status === status) : rows;
  return filtered.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
}

export async function discardDraft(id) {
  const storage = getStorage();
  await storage.put(COLLECTIONS.IMPORTS, {
    ...(await storage.get(COLLECTIONS.IMPORTS, id)),
    status: 'discarded',
    updatedAt: nowISO(),
  });
}

export async function deleteDraft(id) {
  await getStorage().remove(COLLECTIONS.IMPORTS, id);
}

/* ------------------------------ 正式入库 ------------------------------ */

/**
 * 把草稿正式写入内容库
 * @param {string} draftId
 * @param {object} [opts] { textbookId, unitId, createTextbook }
 * @returns {Promise<{textbookId, unitId, counts}>}
 */
export async function commitImport(draftId, opts = {}) {
  const storage = getStorage();
  const draft = await getDraft(draftId);
  if (!draft) throw new Error('草稿不存在');
  if (draft.status === 'committed') throw new Error('该草稿已导入过');

  const textbookId = opts.textbookId || draft.textbookId || uid('tb');
  const unitNo = Number(draft.unit?.no) || 1;
  const unitId = opts.unitId || `${textbookId}-u${unitNo}`;

  // 1) 教材：不存在则新建
  let tb = await storage.get(COLLECTIONS.TEXTBOOKS, textbookId);
  if (!tb) {
    tb = makeTextbook({
      id: textbookId,
      name: opts.textbookName || `导入教材 ${textbookId}`,
      grade: Number(opts.grade) || 4,
      term: opts.term || '上',
      note: '由导入中心创建',
    });
    await storage.put(COLLECTIONS.TEXTBOOKS, tb);
  }

  // 2) 单元
  const unit = makeUnit({
    id: unitId,
    textbookId,
    no: unitNo,
    title: draft.unit?.title || `Unit ${unitNo}`,
    theme: draft.unit?.theme || '',
    objectives: draft.unit?.objectives || [],
    sourcePages: draft.pageCount ? [1, draft.pageCount] : [],
  });
  await storage.put(COLLECTIONS.UNITS, unit);

  // 3) 内容：只导 status='confirmed' 且未被删除的条目
  const alive = list => (list || []).filter(x => !x.removed && x.status !== 'rejected');

  const words = alive(draft.words).filter(w => w.en).map(w => makeWord({
    ...w, id: w.id || uid('w'), textbookId, unitId, status: 'confirmed',
  }));
  const sentences = alive(draft.sentences).filter(s => s.en).map(s => makeSentence({
    ...s, id: s.id || uid('s'), textbookId, unitId, status: 'confirmed',
  }));
  const grammar = alive(draft.grammar).filter(g => g.rule).map(g => makeGrammar({
    ...g, id: g.id || uid('g'), textbookId, unitId, status: 'confirmed',
  }));
  // 题目必须先补齐 refType/refId，否则答错不会进错题本（records.js 会跳过没有 refId 的作答）
  const withRefs = attachQuestionRefs(
    alive(draft.questions).filter(q => q.prompt && q.answer?.length)
      .map(q => ({ ...q, id: q.id || uid('q') })),
    grammar,
  );
  const questions = withRefs.map(q => makeQuestion({
    ...q, textbookId, unitId, status: 'confirmed', origin: q.origin || 'textbook',
  }));

  await Promise.all([
    storage.bulkPut(COLLECTIONS.WORDS, words),
    storage.bulkPut(COLLECTIONS.SENTENCES, sentences),
    storage.bulkPut(COLLECTIONS.GRAMMAR, grammar),
    storage.bulkPut(COLLECTIONS.QUESTIONS, questions),
  ]);

  // 4) 标记草稿已导入
  await storage.put(COLLECTIONS.IMPORTS, {
    ...draft, status: 'committed', committedAt: nowISO(),
    target: { textbookId, unitId },
  });

  invalidateCache(textbookId, unitId);
  emit(EVENTS.CONTENT_CHANGED, { textbookId, unitId });

  return {
    textbookId,
    unitId,
    counts: {
      words: words.length,
      sentences: sentences.length,
      grammar: grammar.length,
      questions: questions.length,
    },
  };
}

/* ------------------------------ 打包/搬运 ------------------------------ */

/** 把某单元现有内容反向打包成草稿结构（用于「把文件教材拷到存储里编辑」） */
export async function packToDraft(content, meta = {}) {
  return {
    id: uid('draft'),
    textbookId: meta.textbookId || '',
    kind: 'textbook',
    sourceFile: meta.sourceName || '来自内置教材',
    pageCount: 0,
    unit: {
      no: content.unit?.no || 1,
      title: content.unit?.title || '',
      theme: content.unit?.theme || '',
      objectives: content.unit?.objectives || [],
      needsReview: false,
    },
    words: (content.words || []).map(w => ({ ...w, needsReview: false, reviewNote: '' })),
    sentences: (content.sentences || []).map(s => ({ ...s, needsReview: false, reviewNote: '' })),
    grammar: (content.grammar || []).map(g => ({ ...g, needsReview: false, reviewNote: '' })),
    questions: (content.questions || []).map(q => ({ ...q, needsReview: false, reviewNote: '' })),
    warnings: [],
    status: 'pending_review',
    stats: {
      words: content.words?.length || 0,
      sentences: content.sentences?.length || 0,
      grammar: content.grammar?.length || 0,
      questions: content.questions?.length || 0,
      needsReview: 0,
    },
    createdAt: nowISO(),
    updatedAt: nowISO(),
  };
}

/** 删除某单元的全部存储版本内容（不影响内置文件） */
export async function deleteUnitContent(textbookId, unitId) {
  const storage = getStorage();
  const [w, s, g, q] = await Promise.all([
    storage.query(COLLECTIONS.WORDS, 'unitId', unitId),
    storage.query(COLLECTIONS.SENTENCES, 'unitId', unitId),
    storage.query(COLLECTIONS.GRAMMAR, 'unitId', unitId),
    storage.query(COLLECTIONS.QUESTIONS, 'unitId', unitId),
  ]);
  await Promise.all([
    storage.bulkRemove(COLLECTIONS.WORDS, w.map(x => x.id)),
    storage.bulkRemove(COLLECTIONS.SENTENCES, s.map(x => x.id)),
    storage.bulkRemove(COLLECTIONS.GRAMMAR, g.map(x => x.id)),
    storage.bulkRemove(COLLECTIONS.QUESTIONS, q.map(x => x.id)),
    storage.remove(COLLECTIONS.UNITS, unitId),
  ]);
  invalidateCache(textbookId, unitId);
  emit(EVENTS.CONTENT_CHANGED, { textbookId, unitId });
}
